/**
 * Moving a site one node-pool stage (JSS#1104): the impact preview from
 * `GET …/node-pools/stage-preview`, then the change. A refusal (one stage at
 * a time, a stage not reached yet, layout problems, no MutatingAdmissionPolicy)
 * is shown and the Confirm button stays disabled.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import type { NodePoolStage, NodePoolsView } from '@jeket-cloud/types';
import { nodePoolsApi } from '@/api/client';
import { STAGE_TEXT, nodePoolsError } from '@/lib/nodePools';

interface Props {
  siteId: string;
  view: NodePoolsView;
  stage: NodePoolStage;
  onClose: () => void;
  onDone: () => void;
}

export default function NodePoolStageDialog({ siteId, view, stage, onClose, onDone }: Props) {
  const preview = useQuery({
    queryKey: ['node-pools-preview', siteId, stage, view.revision],
    queryFn: () => nodePoolsApi.previewStage(siteId, stage),
  });
  const apply = useMutation({
    mutationFn: () => nodePoolsApi.setStage(siteId, stage, view.revision),
    onSuccess: onDone,
  });
  const p = preview.data;
  const refusal = p?.refusal ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="dialog" aria-label={`Move to ${STAGE_TEXT[stage].label}`}>
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-silver-bright font-semibold">
            {STAGE_TEXT[view.stage].label} → {STAGE_TEXT[stage].label}
          </h3>
          <button type="button" aria-label="close" onClick={onClose} className="text-silver-muted hover:text-silver-bright"><X size={16} /></button>
        </div>

        <div className="px-6 py-4 space-y-3 text-xs max-h-[60vh] overflow-y-auto" data-testid="stage-preview">
          <p className="text-silver-muted">{STAGE_TEXT[stage].description}</p>
          {preview.isLoading && <p className="text-silver-muted">Working out what this changes…</p>}
          {preview.error && <p className="text-status-error" role="alert">{nodePoolsError(preview.error).error}</p>}
          {refusal && (
            <div className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 text-status-error" role="alert" data-testid="stage-refusal">
              <p>{refusal.message}</p>
              {Array.isArray(refusal.details?.blocked) && (
                <ul className="mt-1 list-disc list-inside">{(refusal.details!.blocked as string[]).map((b) => <li key={b}>{b}</li>)}</ul>
              )}
              {Array.isArray(refusal.details?.problems) && (
                <ul className="mt-1 list-disc list-inside">
                  {(refusal.details!.problems as { message: string }[]).map((x) => <li key={x.message}>{x.message}</li>)}
                </ul>
              )}
            </div>
          )}
          {p && p.effects.length > 0 && (
            <div>
              <p className="font-semibold text-silver-bright mb-1">What happens</p>
              <ol className="list-decimal list-inside space-y-1 text-silver-bright" data-testid="stage-effects">
                {p.effects.map((e) => <li key={e}>{e}</li>)}
              </ol>
            </div>
          )}
          {p && p.daemonSetGaps.some((g) => !g.known) && (
            <p className="flex items-start gap-1.5 text-status-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              Until these tolerate the pool taint, their nodes stay untainted: {p.daemonSetGaps.filter((g) => !g.known).map((g) => `${g.namespace}/${g.name}`).join(', ')}.
            </p>
          )}
          {p && p.podsMisplacedAfter.length > 0 && (
            <div>
              <p className="font-semibold text-silver-bright mb-1">
                {p.podsMisplacedAfter.length} running pod{p.podsMisplacedAfter.length === 1 ? '' : 's'} would then be out of place
              </p>
              <p className="text-silver-muted mb-1">They keep running; reschedule them from Conformance when you are ready.</p>
              <ul className="font-mono text-silver-muted space-y-0.5" data-testid="stage-misplaced">
                {p.podsMisplacedAfter.slice(0, 20).map((i) => <li key={`${i.namespace}/${i.pod}`}>{i.namespace}/{i.pod} on {i.node}</li>)}
                {p.podsMisplacedAfter.length > 20 && <li>…and {p.podsMisplacedAfter.length - 20} more</li>}
              </ul>
            </div>
          )}
          {apply.error && <p className="text-status-error" role="alert">{nodePoolsError(apply.error).error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright">Cancel</button>
          <button
            type="button"
            onClick={() => apply.mutate()}
            disabled={!p || refusal !== null || apply.isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {apply.isPending ? 'Applying…' : `Move to ${STAGE_TEXT[stage].label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
